import { computed } from 'vue'
import { usePropertyDetailQuery } from '../api/usePropertyDetailQuery'
import { DEFAULT_PROPERTY_PROCESS_STEPS } from '../constants/propertyDefaults'
import {
  PROPERTY_PLACEHOLDER_IMAGE,
  buildAbout,
  buildAmenities,
  buildDocuments,
  buildGallery,
  buildHeader,
  buildHighlights,
  buildLocation,
  type PropertyDetailData,
} from '../utils/propertyDetailUtils'
import { buildCalculatorConfig } from '../utils/propertyCalculatorUtils'
import {
  buildInvestmentBreakdown,
  buildPropertyFinancials,
  buildReturnTiles,
} from '../utils/propertyFinancialUtils'

// Subscribes to the detail query and exposes the page's per-section view
// models. Stateless — no refs, no mutations. For bookmark state, compose
// with usePropertyBookmark in the same container.
export const usePropertyDetailViewModel = (propertyId: string) => {
  const query = usePropertyDetailQuery(propertyId)
  const raw = computed<PropertyDetailData | null>(() => query.data.value ?? null)

  return {
    raw,
    loading: computed(() => query.isLoading.value),
    error: computed(() => query.error.value?.message ?? null),
    offeringId: computed(() => raw.value?.offeringId ?? ''),
    status: computed(() => raw.value?.propertyStatus ?? ''),
    gallery: computed(() => buildGallery(raw.value)),
    header: computed(() => buildHeader(raw.value)),
    breakdown: computed(() => buildInvestmentBreakdown(raw.value)),
    propertyFinancials: computed(() => buildPropertyFinancials(raw.value)),
    calculator: computed(() => buildCalculatorConfig(raw.value)),
    returnTiles: computed(() => buildReturnTiles(raw.value)),
    about: computed(() => buildAbout(raw.value)),
    amenities: computed(() => buildAmenities(raw.value)),
    highlights: computed(() => buildHighlights(raw.value)),
    location: computed(() => buildLocation(raw.value)),
    documents: computed(() => buildDocuments(raw.value)),
    processSteps: DEFAULT_PROPERTY_PROCESS_STEPS,
    propertyPlaceholder: PROPERTY_PLACEHOLDER_IMAGE,
  }
}
